import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit2, X, Loader2, Package, ExternalLink, Trash2, Save, AlertCircle, Ruler, RefreshCw, Download, Search } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { businessToday } from '../lib/dates'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts'
import {
  getPackagingSummary, getPackagingTimeseries,
  upsertPackageType, deletePackageType, rematchPackaging,
  listPackageInventory, createPackageInventory, updatePackageInventory, deletePackageInventory,
  listShipstationCustomPackages, importShipstationPackages,
  listPackageTypes,
} from '../lib/api'
import { ToastContainer, useToast } from './Toast'
import { useAuth } from '../hooks/useAuth'

const TODAY = () => businessToday()

// Distinct, readable colours for the chart lines
const LINE_COLOURS = [
  '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#475569',
]

function fmtNum(n) { return new Intl.NumberFormat('en-AU').format(n ?? 0) }
function fmtDim(v) { return v == null ? '' : (Number.isInteger(v) ? v : Number(v).toFixed(2).replace(/\.?0+$/, '')) }

// Sentinel — must match OTHER_BUCKET_CODE in backend/routers/admin.py.
// Used to distinguish the synthetic "Other" row from real user-defined packages.
const OTHER_CODE = '__other__'

// Slugify a friendly name into a stable package_code (e.g. "BEETL - Large White Box" → "beetl_large_white_box")
function slugify(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
}

// Best label for a row: display name if set, otherwise the raw code
function pkgLabel(row) {
  return (row?.display_name && row.display_name.trim()) || row?.package_code || ''
}

// ─── Edit-metadata modal (also handles "add new" when row.package_code is blank) ─

function EditMetaModal({ clientId, row, onClose, isNew = false }) {
  const [displayName, setDisplayName] = useState(row.display_name || '')
  const [websiteName, setWebsiteName] = useState(row.website_name || '')
  const [reorderUrl,  setReorderUrl]  = useState(row.reorder_url || '')
  const [dimLength,   setDimLength]   = useState(row.dim_length ?? '')
  const [dimWidth,    setDimWidth]    = useState(row.dim_width  ?? '')
  const [dimHeight,   setDimHeight]   = useState(row.dim_height ?? '')
  const [dimUnits,    setDimUnits]    = useState(row.dim_units || 'inches')
  const [lowStockThreshold, setLowStockThreshold] = useState(row.low_stock_threshold ?? '')
  const [linkedToCodes, setLinkedToCodes]         = useState(
    Array.isArray(row.linked_to_codes) && row.linked_to_codes.length > 0
      ? row.linked_to_codes
      : (row.linked_to_code ? [row.linked_to_code] : [])
  )
  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const myEmail = user?.email || ''

  // Pull the client's existing package list to populate the link-to dropdown.
  // Filter to the user's own defined packages (excludes the synthetic Other
  // bucket) — and rule out self + packages that are themselves already linked
  // to something (1-level chain).
  const { data: existingTypes = [] } = useQuery({
    queryKey: ['packaging', 'types', clientId],
    queryFn:  () => listPackageTypes(clientId),
    enabled:  !!clientId,
  })
  const linkOptions = existingTypes.filter(t => {
    if (!t.package_code || t.package_code === row.package_code) return false
    // A target is "itself linked" if its array is non-empty OR (legacy) its
    // deprecated scalar is set. Once we drop `linked_to_code`, the array
    // check alone is enough.
    const arrLinked = Array.isArray(t.linked_to_codes) && t.linked_to_codes.filter(Boolean).length > 0
    const scalarLinked = !!(t.linked_to_code && t.linked_to_code.trim())
    return !(arrLinked || scalarLinked)
  })

  // For new packages: auto-derive code from name. For existing: keep the original code.
  const packageCode = isNew ? slugify(displayName) : row.package_code

  const mutation = useMutation({
    mutationFn: () => upsertPackageType(clientId, packageCode, {
      display_name: displayName.trim() || null,
      website_name: websiteName.trim() || null,
      reorder_url:  reorderUrl.trim()  || null,
      dim_length:   dimLength === '' ? null : Number(dimLength),
      dim_width:    dimWidth  === '' ? null : Number(dimWidth),
      dim_height:   dimHeight === '' ? null : Number(dimHeight),
      dim_units:    dimUnits || null,
      low_stock_threshold: lowStockThreshold === '' ? null : parseInt(lowStockThreshold, 10),
      linked_to_codes:     linkedToCodes,
      // Back-compat during rollout: backend accepts either shape.
      linked_to_code:      linkedToCodes[0] || null,
      // Email is auto-set server-side to the admin's email — no input needed.
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging', 'summary', clientId] })
      queryClient.invalidateQueries({ queryKey: ['packaging', 'types',   clientId] })
      onClose()
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to save' }),
  })

  const deleteMut = useMutation({
    mutationFn: () => deletePackageType(clientId, row.package_code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging', 'summary', clientId] })
      queryClient.invalidateQueries({ queryKey: ['packaging', 'types',   clientId] })
      onClose()
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to delete' }),
  })

  function handleSubmit(e) {
    e.preventDefault()
    if (!displayName.trim()) {
      addToast({ type: 'error', message: 'Package name is required' })
      return
    }
    if (isNew && !packageCode) {
      addToast({ type: 'error', message: 'Name must contain at least one letter or number' })
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
            <div>
              <h3 className="font-semibold text-slate-800">{isNew ? 'Add Package' : 'Edit Package'}</h3>
              {!isNew && row.package_code && (
                <p className="text-xs text-slate-300 mt-0.5 font-mono">{row.package_code}</p>
              )}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className="label">Package name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                className="input"
                placeholder="e.g. BEETL - Large White Box"
                autoFocus
                required
              />
              <p className="text-xs text-slate-400 mt-1">
                The name shown in this portal. {isNew && 'Match it to your ShipStation custom package name.'}
              </p>
            </div>

            <div>
              <label className="label">Website name</label>
              <input
                type="text"
                value={websiteName}
                onChange={e => setWebsiteName(e.target.value)}
                className="input"
                placeholder="e.g. Mailing Box Large 250 x 175 x 95mm"
              />
              <p className="text-xs text-slate-400 mt-1">
                The exact product name as it appears on the supplier's website — useful for reorders.
              </p>
            </div>

            <div>
              <label className="label flex items-center gap-1.5 text-slate-500">
                <Ruler size={12} /> Dimensions
                <span className="font-normal text-slate-400 lowercase">(used to auto-tag matching orders)</span>
              </label>
              <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                <input
                  type="number" min="0" step="0.01"
                  value={dimLength} onChange={e => setDimLength(e.target.value)}
                  className="input text-sm" placeholder="L"
                />
                <input
                  type="number" min="0" step="0.01"
                  value={dimWidth} onChange={e => setDimWidth(e.target.value)}
                  className="input text-sm" placeholder="W"
                />
                <input
                  type="number" min="0" step="0.01"
                  value={dimHeight} onChange={e => setDimHeight(e.target.value)}
                  className="input text-sm" placeholder="H"
                />
                <select
                  value={dimUnits} onChange={e => setDimUnits(e.target.value)}
                  className="input text-sm w-20"
                >
                  <option value="inches">in</option>
                  <option value="centimeters">cm</option>
                  <option value="millimeters">mm</option>
                </select>
              </div>
            </div>

            <div>
              <label className="label">Reorder URL</label>
              <input
                type="url"
                value={reorderUrl}
                onChange={e => setReorderUrl(e.target.value)}
                className="input"
                placeholder="https://supplier.example.com/product"
              />
            </div>

            {/* Trigger package: when set, THIS package depletes 1-for-1
                every time the chosen trigger is used in an order. Use case:
                "Hat Insert" (manually defined, not in ShipStation) follows
                "ATG - Hat Box" — one insert consumed per box shipped.
                Inventory stays this package's own; usage mirrors the trigger's. */}
            <div>
              <label className="label">Depletes when another package is used</label>
              {linkedToCodes.length === 0 && (
                <p className="text-xs text-slate-400 italic mb-1">
                  — Stand-alone (no triggers) —
                </p>
              )}
              <div className="border border-slate-200 rounded-lg max-h-[200px] overflow-y-auto divide-y divide-slate-100">
                {linkOptions.length === 0 ? (
                  <p className="text-xs text-slate-400 px-3 py-2">
                    No eligible trigger packages available.
                  </p>
                ) : linkOptions.map(opt => {
                  const checked = linkedToCodes.includes(opt.package_code)
                  return (
                    <label
                      key={opt.package_code}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          if (e.target.checked) {
                            setLinkedToCodes(prev => [...prev, opt.package_code])
                          } else {
                            setLinkedToCodes(prev => prev.filter(c => c !== opt.package_code))
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-slate-700">
                        {opt.display_name || opt.package_code}
                      </span>
                    </label>
                  )
                })}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Optional. Pick one or more parent packages whose usage drives this
                one — this package depletes once for EACH order that uses ANY of
                the checked triggers. Inventory stays separate.
              </p>
            </div>

            {/* Low-stock alert — emails the admin who set the threshold
                when balance ≤ threshold. The recipient is always the
                logged-in user; clear the threshold to disable. */}
            <div className="pt-2 border-t border-slate-100">
              <label className="label flex items-center gap-1.5 text-slate-500">
                <AlertCircle size={12} /> Low-stock alert
                <span className="font-normal text-slate-400 lowercase">(optional)</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={lowStockThreshold}
                  onChange={e => setLowStockThreshold(e.target.value)}
                  className="input text-sm w-28"
                  placeholder="Qty"
                />
                <span className="text-xs text-slate-500">
                  Emails {myEmail ? <b className="text-slate-700">{myEmail}</b> : 'you'} when balance ≤ threshold
                </span>
              </div>
              {row.low_stock_email && row.low_stock_email !== myEmail && (
                <p className="text-xs text-amber-600 mt-1">
                  Currently armed for <b>{row.low_stock_email}</b>. Saving will switch the recipient to you.
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                Fires once per dip; re-arms when the balance climbs back above the threshold. Clear the qty to disable.
              </p>
            </div>

            <div className="flex justify-between items-center gap-2 pt-2">
              {!isNew ? (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete custom package "${row.display_name || row.package_code}"? Orders will keep their current package_code value but won't auto-match this package any more.`)) {
                      deleteMut.mutate()
                    }
                  }}
                  className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                >
                  <Trash2 size={14} /> Delete
                </button>
              ) : <div />}
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={mutation.isPending} className="btn-primary">
                  {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ─── Inventory section ──────────────────────────────────────────────────────

function InventorySection({ clientId, packageCodes, codeToLabel = {} }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  // Drop the synthetic "Other" bucket — receipts can only be logged against
  // user-defined packages.
  const selectableCodes = packageCodes.filter(c => c !== OTHER_CODE)

  const [packageCode, setPackageCode] = useState('')
  const [quantity, setQuantity] = useState('')
  const [receivedDate, setReceivedDate] = useState(TODAY())
  const [notes, setNotes] = useState('')

  const { data: entries = [], isLoading, error: entriesError } = useQuery({
    queryKey: ['packaging', 'inventory', clientId],
    queryFn: () => listPackageInventory(clientId),
    enabled: !!clientId,
  })

  const createMut = useMutation({
    mutationFn: (data) => createPackageInventory(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging', 'summary', clientId] })
      queryClient.invalidateQueries({ queryKey: ['packaging', 'inventory', clientId] })
      setPackageCode(''); setQuantity(''); setNotes('')
      addToast({ type: 'success', message: 'Receipt recorded' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to add receipt' }),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => deletePackageInventory(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging', 'summary', clientId] })
      queryClient.invalidateQueries({ queryKey: ['packaging', 'inventory', clientId] })
      addToast({ type: 'success', message: 'Receipt removed' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to delete' }),
  })

  function handleAdd(e) {
    e.preventDefault()
    const code = packageCode
    const qty = parseInt(quantity, 10)
    if (!code) { addToast({ type: 'error', message: 'Choose a package' }); return }
    if (!qty || qty <= 0) { addToast({ type: 'error', message: 'Quantity must be > 0' }); return }
    createMut.mutate({
      package_code: code,
      quantity: qty,
      received_date: receivedDate || null,
      notes: notes.trim() || null,
    })
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700">Record New Stock Received</h3>
        <p className="text-xs text-slate-400 mt-0.5">Add an entry whenever new packaging arrives so the inventory balance stays accurate.</p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="px-4 py-4 grid grid-cols-1 md:grid-cols-[1.4fr_0.6fr_0.8fr_1.4fr_auto] gap-3 items-end border-b border-slate-100">
        <div>
          <label className="label">Package</label>
          <select
            value={packageCode}
            onChange={e => setPackageCode(e.target.value)}
            className="input"
            required
          >
            <option value="" disabled>Select…</option>
            {selectableCodes.map(c => <option key={c} value={c}>{codeToLabel[c] || c}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Quantity</label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            className="input"
            placeholder="0"
            required
          />
        </div>
        <div>
          <label className="label">Received</label>
          <input
            type="date"
            value={receivedDate}
            onChange={e => setReceivedDate(e.target.value)}
            className="input"
            required
          />
        </div>
        <div>
          <label className="label">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="input"
            placeholder="PO #, supplier, etc."
          />
        </div>
        <button type="submit" disabled={createMut.isPending} className="btn-primary justify-center h-[38px]">
          {createMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>

      {/* History list */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Received</th>
              <th className="text-left px-4 py-2.5">Package</th>
              <th className="text-right px-4 py-2.5">Qty</th>
              <th className="text-left px-4 py-2.5">Notes</th>
              <th className="px-4 py-2.5 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td colSpan="5" className="px-4 py-6 text-center text-slate-400">
                <Loader2 size={16} className="inline animate-spin" /> Loading…
              </td></tr>
            )}
            {/* On failure `entries` keeps its [] default, so without this row a
                broken request renders as "No receipts recorded yet." */}
            {!isLoading && entriesError && (
              <tr><td colSpan="5" className="px-4 py-6 text-center">
                <AlertCircle size={16} className="inline mr-1 -mt-0.5 text-red-500" />
                <span className="text-sm font-medium text-slate-700">Couldn't load receipts</span><br />
                <span className="text-xs text-slate-500">This is not an empty list — the request failed.</span><br />
                <span className="text-xs text-red-600 font-mono break-all">
                  {entriesError?.response?.data?.detail || entriesError?.message || 'Unknown error'}
                </span>
              </td></tr>
            )}
            {!isLoading && !entriesError && entries.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-6 text-center text-slate-400">
                No receipts recorded yet.
              </td></tr>
            )}
            {entries.map(e => (
              <tr key={e.id} className="hover:bg-slate-50">
                <td className="px-4 py-2 text-slate-600">{format(parseISO(e.received_date), 'dd MMM yyyy')}</td>
                <td className="px-4 py-2 text-slate-700">{codeToLabel[e.package_code] || e.package_code}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtNum(e.quantity)}</td>
                <td className="px-4 py-2 text-slate-500">{e.notes || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete receipt of ${e.quantity} × ${e.package_code} on ${format(parseISO(e.received_date), 'dd MMM yyyy')}?`)) {
                        deleteMut.mutate(e.id)
                      }
                    }}
                    className="text-slate-300 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ─── Import-from-ShipStation modal ──────────────────────────────────────────

function ImportShipstationModal({ clientId, clientName, onClose }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(() => new Set())

  const { data: packages = [], isLoading, error } = useQuery({
    queryKey: ['shipstation', 'custom-packages'],
    queryFn: listShipstationCustomPackages,
    staleTime: 5 * 60_000,   // 5 min — list changes infrequently
  })

  // Pre-check packages whose name contains the client name (or first word of it)
  const clientFirstWord = (clientName || '').split(/\s+/)[0]
  const presetSelected = useMemo(() => {
    if (!packages.length || !clientName) return new Set()
    const lc = clientName.toLowerCase()
    const lcFirst = (clientFirstWord || '').toLowerCase()
    const out = new Set()
    packages.forEach(p => {
      const n = (p.name || '').toLowerCase()
      if (n.includes(lc) || (lcFirst && n.includes(lcFirst))) out.add(p.package_id)
    })
    return out
  }, [packages, clientName, clientFirstWord])

  // Apply preset only on first load
  useEffect(() => {
    if (presetSelected.size > 0 && selected.size === 0) {
      setSelected(new Set(presetSelected))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetSelected])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return packages
    return packages.filter(p => (p.name || '').toLowerCase().includes(q))
  }, [packages, search])

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setSelected(prev => {
      const next = new Set(prev)
      filtered.forEach(p => next.add(p.package_id))
      return next
    })
  }

  function clearAll() {
    setSelected(new Set())
  }

  const importMut = useMutation({
    mutationFn: () => importShipstationPackages(clientId, Array.from(selected)),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['packaging', 'summary', clientId] })
      addToast({ type: 'success', message: `Imported ${data.imported} package${data.imported === 1 ? '' : 's'}.` })
      onClose()
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Import failed' }),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[85vh]">
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 flex-shrink-0">
            <div>
              <h3 className="font-semibold text-slate-800">Import from ShipStation</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Pulls every Custom Package defined in your ShipStation account. Pick which belong to <b>{clientName}</b>.
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>

          {/* Search + bulk actions */}
          <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${packages.length} packages…`}
                className="input pl-9 text-sm"
              />
            </div>
            <button onClick={selectAllVisible} type="button" className="text-xs text-blue-600 hover:text-blue-700 px-2">
              Select all visible
            </button>
            <button onClick={clearAll} type="button" className="text-xs text-slate-500 hover:text-slate-700 px-2">
              Clear
            </button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-6 py-3">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle size={14} className="inline mr-1" />
                {error.response?.data?.detail || 'Failed to load packages from ShipStation.'}
                {error.response?.status === 503 && (
                  <p className="mt-2 text-xs">
                    The v2 API key is not configured. Add <code className="font-mono">SHIPSTATION_V2_API_KEY</code> to
                    your environment variables and restart the backend.
                  </p>
                )}
              </div>
            )}
            {isLoading && (
              <div className="py-8 text-center text-slate-400">
                <Loader2 size={20} className="inline animate-spin" /> Loading packages from ShipStation…
              </div>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <div className="py-8 text-center text-slate-400 text-sm">No packages match your search.</div>
            )}
            {!isLoading && filtered.map(p => {
              const checked = selected.has(p.package_id)
              const dimsLabel = p.dim_length && p.dim_width && p.dim_height
                ? `${fmtDim(p.dim_length)} × ${fmtDim(p.dim_width)} × ${fmtDim(p.dim_height)} ${p.dim_units || ''}`
                : <span className="text-amber-600">no dimensions</span>
              return (
                <label key={p.package_id}
                  className={`flex items-start gap-3 p-2.5 rounded cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(p.package_id)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400 tabular-nums">{dimsLabel}</div>
                  </div>
                </label>
              )
            })}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center flex-shrink-0">
            <p className="text-xs text-slate-500">
              {selected.size} of {packages.length} selected
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button
                type="button"
                onClick={() => importMut.mutate()}
                disabled={importMut.isPending || selected.size === 0}
                className="btn-primary"
              >
                {importMut.isPending
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Download size={14} />}
                Import {selected.size > 0 ? `(${selected.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ─── Main tab ───────────────────────────────────────────────────────────────

export default function PackagingTab({ clientId, clientName }) {
  const [chartFilter, setChartFilter] = useState('all')
  const [editing, setEditing] = useState(null)
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const rematchMut = useMutation({
    mutationFn: () => rematchPackaging(clientId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['packaging', 'summary', clientId] })
      queryClient.invalidateQueries({ queryKey: ['packaging', 'timeseries', clientId] })
      addToast({
        type: 'success',
        message: `Re-matched ${data.matched_and_updated} orders (${data.unmatched} dims didn't match any package).`,
      })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Re-match failed' }),
  })

  const { data: summary = [], isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['packaging', 'summary', clientId],
    queryFn: () => getPackagingSummary(clientId),
    enabled: !!clientId,
  })

  // Full metadata for every package_type on this client — used by the
  // Edit modal (link-to dropdown) and by the alias chips on parent rows
  // so clicking an alias opens its own Edit modal.
  const { data: allTypes = [] } = useQuery({
    queryKey: ['packaging', 'types', clientId],
    queryFn: () => listPackageTypes(clientId),
    enabled: !!clientId,
  })
  const typesByCode = useMemo(() => {
    const out = {}
    for (const t of allTypes) {
      if (t.package_code) out[t.package_code] = t
    }
    return out
  }, [allTypes])

  const { data: series = [], isLoading: seriesLoading, error: seriesError } = useQuery({
    queryKey: ['packaging', 'timeseries', clientId, chartFilter],
    queryFn: () => getPackagingTimeseries(clientId, chartFilter !== 'all' ? { package_code: chartFilter } : {}),
    enabled: !!clientId,
  })

  // Pivot timeseries into { date, code1: count, code2: count, code1__ma: avg, ... } for recharts.
  // Each package gets both a daily count and a 7-day trailing moving average for the trend line.
  const MA_WINDOW = 7
  const { chartData, packageList } = useMemo(() => {
    if (series.length === 0) return { chartData: [], packageList: [] }
    const codes = Array.from(new Set(series.map(p => p.package_code))).sort()
    const byDate = new Map()
    series.forEach(p => {
      const day = p.date
      const existing = byDate.get(day) || { date: day }
      existing[p.package_code] = (existing[p.package_code] || 0) + p.count
      byDate.set(day, existing)
    })
    // Fill zeros for any missing combo so chart lines stay continuous
    const sortedRows = Array.from(byDate.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(row => {
        const out = { date: row.date }
        codes.forEach(c => { out[c] = row[c] ?? 0 })
        return out
      })
    // Compute trailing moving average per package (window = MA_WINDOW days).
    // Stops the noisy daily line dominating the read — the smoothed line shows trend.
    const data = sortedRows.map((row, i) => {
      const out = { ...row }
      const start = Math.max(0, i - MA_WINDOW + 1)
      const window = sortedRows.slice(start, i + 1)
      codes.forEach(c => {
        const sum = window.reduce((s, r) => s + (r[c] || 0), 0)
        out[`${c}__ma`] = Math.round((sum / window.length) * 100) / 100
      })
      return out
    })
    return { chartData: data, packageList: codes }
  }, [series])

  const allPackageCodes = useMemo(
    () => Array.from(new Set(summary.map(r => r.package_code))).sort(),
    [summary],
  )

  // Map raw package_code → display name (fallback to code) for the chart legend + filter
  const codeToLabel = useMemo(() => {
    const m = {}
    summary.forEach(r => { m[r.package_code] = pkgLabel(r) })
    return m
  }, [summary])

  return (
    // key={clientId} on the outer wrapper — if the parent switches clients
    // mid-edit, React remounts the whole tree instead of leaving an open
    // EditMetaModal bound to the previous client's package_code.
    <div key={clientId} className="space-y-4">
      {/* Summary table */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 gap-2 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Package Summary</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Custom packages are matched to orders by dimensions (ShipStation's API doesn't
              expose package selection). Run rates show orders per week.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setImporting(true)}
              className="btn-secondary text-xs py-1.5"
              title="Pull custom packages directly from ShipStation"
            >
              <Download size={12} /> Import from ShipStation
            </button>
            <button
              onClick={() => rematchMut.mutate()}
              disabled={rematchMut.isPending}
              className="btn-secondary text-xs py-1.5"
              title="Re-evaluate package_code on every existing order based on current package definitions"
            >
              {rematchMut.isPending
                ? <Loader2 size={12} className="animate-spin" />
                : <RefreshCw size={12} />} Re-match orders
            </button>
            <button
              onClick={() => setAdding(true)}
              className="btn-primary text-xs py-1.5"
            >
              <Plus size={12} /> Define new package
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Package</th>
                <th className="text-right px-4 py-2.5">Balance</th>
                <th className="text-right px-4 py-2.5">1w / wk</th>
                <th className="text-right px-4 py-2.5">2w / wk</th>
                <th className="text-right px-4 py-2.5">4w / wk</th>
                <th className="text-right px-4 py-2.5">8w / wk</th>
                <th className="px-4 py-2.5 w-12" />
                <th className="px-4 py-2.5 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summaryLoading && (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                  <Loader2 size={16} className="inline animate-spin" /> Loading…
                </td></tr>
              )}
              {/* `summary` falls back to [] when the fetch fails, which would
                  otherwise render the "No packages yet" row below — a broken
                  request reading as a client with no packaging, no balances
                  and no low-stock alerts to act on. */}
              {!summaryLoading && summaryError && (
                <tr><td colSpan="8" className="px-4 py-8 text-center">
                  <AlertCircle size={20} className="inline-block mb-2 text-red-500" /><br />
                  <span className="text-sm font-medium text-slate-700">Couldn't load packages</span><br />
                  <span className="text-xs text-slate-500">
                    This is <b>not</b> an empty list — balances and low-stock alerts are unknown.
                  </span><br />
                  <span className="text-xs text-red-600 font-mono break-all">
                    {summaryError?.response?.data?.detail || summaryError?.message || 'Unknown error'}
                  </span>
                </td></tr>
              )}
              {!summaryLoading && !summaryError && summary.length === 0 && (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                  <Package size={20} className="inline-block mb-2 text-slate-300" /><br />
                  No packages yet. Click <b>Define new package</b> to add your first one.
                </td></tr>
              )}
              {summary.map(row => {
                const isOther = row.package_code === OTHER_CODE
                const label = pkgLabel(row)
                const dimsLabel = row.dim_length && row.dim_width && row.dim_height
                  ? `${fmtDim(row.dim_length)} × ${fmtDim(row.dim_width)} × ${fmtDim(row.dim_height)} ${row.dim_units || ''}`
                  : null
                // Low-stock alert state:
                //   threshold configured + balance at or below → "Low" badge.
                //   Already-alerted is a separate fact (alerted_at present) — surface
                //   it via tooltip so the admin knows an email already went out.
                const hasThreshold = !isOther && typeof row.low_stock_threshold === 'number'
                const isLow       = hasThreshold && row.balance <= row.low_stock_threshold
                const alreadyAlerted = isLow && !!row.low_stock_alerted_at
                return (
                  <tr key={row.package_code} className={`${isOther ? 'bg-slate-50/50' : isLow ? 'bg-red-50/30 hover:bg-red-50/40' : 'hover:bg-slate-50'}`}>
                    <td className="px-4 py-2.5">
                      <div className={`font-medium ${isOther ? 'text-slate-500 italic' : 'text-slate-800'}`}>
                        {label}
                      </div>
                      {isOther ? (
                        <div className="text-xs text-slate-400 mt-0.5">Orders not matched to any defined package</div>
                      ) : (
                        <>
                          {row.website_name && (
                            <div className="text-xs text-slate-500 mt-0.5">{row.website_name}</div>
                          )}
                          {dimsLabel && (
                            <div className="text-xs text-slate-400 mt-0.5 tabular-nums">{dimsLabel}</div>
                          )}
                          {hasThreshold && (
                            <div className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1">
                              <AlertCircle size={10} />
                              Alert at ≤ {row.low_stock_threshold}
                              {row.low_stock_email && (
                                <span className="text-slate-300">· {row.low_stock_email}</span>
                              )}
                            </div>
                          )}
                          {/* Dependent row: shows which trigger(s) drive its usage.
                              Multi-select — a package can list many triggers, so
                              render each as a clickable chip. Legacy rows still
                              on the scalar fall back to a single-element list. */}
                          {(() => {
                            const triggers = (Array.isArray(row.linked_to_codes) && row.linked_to_codes.length > 0)
                              ? row.linked_to_codes.filter(Boolean)
                              : (row.linked_to_code ? [row.linked_to_code] : [])
                            if (triggers.length === 0) return null
                            return (
                              <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-1">
                                <span>↗ Depletes with</span>
                                {triggers.map((tc, i) => (
                                  <span key={tc} className="inline-flex items-center">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const full = typesByCode[tc]
                                        setEditing(full || { package_code: tc, display_name: tc })
                                      }}
                                      className="text-blue-500 hover:text-blue-700 hover:underline"
                                      title={`Edit ${tc}`}
                                    >
                                      {typesByCode[tc]?.display_name || tc}
                                    </button>
                                    {i < triggers.length - 1 && <span className="ml-0.5">,</span>}
                                  </span>
                                ))}
                              </div>
                            )
                          })()}
                          {/* Trigger row: shows which dependents follow this package's usage.
                              Click a dependent name to edit it (e.g. to break the link). */}
                          {row.linked_aliases && row.linked_aliases.length > 0 && (
                            <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-1">
                              <span>↗ Also depletes</span>
                              {row.linked_aliases.map((dep, i) => (
                                <span key={dep.package_code} className="inline-flex items-center">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const full = typesByCode[dep.package_code]
                                      setEditing(full || { package_code: dep.package_code, display_name: dep.display_name })
                                    }}
                                    className="text-blue-500 hover:text-blue-700 hover:underline"
                                    title={`Edit ${dep.display_name}`}
                                  >
                                    {dep.display_name}
                                  </button>
                                  {i < row.linked_aliases.length - 1 && <span className="ml-0.5">,</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                      isOther ? 'text-slate-300' :
                      row.balance < 0 ? 'text-red-600' :
                      isLow ? 'text-red-600' :
                      row.balance < 50 ? 'text-amber-600' : 'text-slate-700'
                    }`}>
                      {isOther ? '—' : (
                        <span className="inline-flex items-center justify-end gap-1.5">
                          {(row.balance < 0 || isLow) && <AlertCircle size={11} className="-mt-0.5" />}
                          {fmtNum(row.balance)}
                          {isLow && (
                            <span
                              className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                              title={alreadyAlerted ? `Alert email sent ${new Date(row.low_stock_alerted_at).toLocaleString()}` : 'Will email on next summary view'}
                            >
                              {alreadyAlerted ? 'Notified' : 'Low'}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{row.run_rate_1w?.toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{row.run_rate_2w?.toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{row.run_rate_4w?.toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{row.run_rate_8w?.toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {isOther ? <span className="text-slate-200">—</span> :
                       row.reorder_url ? (
                        <a href={row.reorder_url} target="_blank" rel="noopener noreferrer"
                           className="text-blue-600 hover:text-blue-700 inline-flex items-center"
                           title="Open reorder link">
                          <ExternalLink size={14} />
                        </a>
                      ) : <span className="text-slate-200">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {!isOther && (
                        <button
                          onClick={() => setEditing(row)}
                          className="text-slate-300 hover:text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Chart */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Usage Trend</h3>
            <p className="text-xs text-slate-400">
              Daily orders by package (last 8 weeks). Dashed lines show the 7-day rolling average.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mr-2">Filter:</label>
            <select
              value={chartFilter}
              onChange={e => setChartFilter(e.target.value)}
              className="input py-1 text-sm w-auto inline-block"
            >
              <option value="all">All packages</option>
              {allPackageCodes.map(c => <option key={c} value={c}>{codeToLabel[c] || c}</option>)}
            </select>
          </div>
        </div>

        {seriesLoading ? (
          <div className="h-72 flex items-center justify-center text-slate-400">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : seriesError ? (
          /* An empty `series` is the default on failure, so the "no usage data"
             state below would otherwise report a dead request as zero usage. */
          <div className="h-72 flex flex-col items-center justify-center text-center px-4">
            <AlertCircle size={20} className="text-red-500 mb-2" />
            <p className="text-sm font-medium text-slate-700">Couldn't load usage data</p>
            <p className="text-xs text-slate-500 mt-0.5">
              This is <b>not</b> zero usage — the request failed.
            </p>
            <p className="text-xs text-red-600 font-mono mt-1 break-all">
              {seriesError?.response?.data?.detail || seriesError?.message || 'Unknown error'}
            </p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-slate-400 text-sm">
            No usage data in the selected window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={d => format(parseISO(d), 'd MMM')} minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                labelFormatter={d => format(parseISO(d), 'EEE d MMM yyyy')}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {packageList.map((c, i) => (
                <Line
                  key={c}
                  type="monotone"
                  dataKey={c}
                  name={codeToLabel[c] || c}
                  stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
              {/* 7-day trailing moving-average overlay per package — dashed and lighter */}
              {packageList.map((c, i) => (
                <Line
                  key={`${c}__ma`}
                  type="monotone"
                  dataKey={`${c}__ma`}
                  name={`${codeToLabel[c] || c} (7-day avg)`}
                  stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.55}
                  dot={false}
                  activeDot={false}
                  legendType="plainline"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Inventory section */}
      <InventorySection clientId={clientId} packageCodes={allPackageCodes} codeToLabel={codeToLabel} />

      {/* Edit-meta modal */}
      {editing && (
        <EditMetaModal
          clientId={clientId}
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Define-new-package modal */}
      {adding && (
        <EditMetaModal
          clientId={clientId}
          row={{ package_code: '', display_name: '', reorder_url: '', dim_units: 'inches' }}
          isNew
          onClose={() => setAdding(false)}
        />
      )}

      {/* Import-from-ShipStation modal */}
      {importing && (
        <ImportShipstationModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setImporting(false)}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}
